/**
 * Keeps reusable alias and email-list entry behavior consistent while allowing
 * domain-specific validators to replace the internal Brass lookup.
 */
import React, { useState } from 'react';
import { useController } from 'react-hook-form';
import Button from '@amzn/meridian/button';
import Column from '@amzn/meridian/column';
import Input from '@amzn/meridian/input';
import Row from '@amzn/meridian/row';
import Alert from '@amzn/meridian/alert';
import Loader from '@amzn/meridian/loader';
import Text from '@amzn/meridian/text';
import { useTranslation } from 'src/arb-wrapper';
import { useLazyValidateAliasQuery } from 'src/store/leela-api';
import type { FormRhfControllerProps } from './types';
import { TagCloud } from '../TagCloud';

const ALIAS_REGEX = /^[a-z][a-z0-9-]{0,31}$/;
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

interface FormSharedUserInputProps extends FormRhfControllerProps {
  required?: boolean;
  currentUser?: string;
  label?: string;
  /** Content rendered inline after the visible label, such as contextual guidance. */
  labelAddon?: React.ReactNode;
  /** When true, requires and stores an exact email address because caller identity matching is domain-sensitive. */
  requireEmail?: boolean;
  /** When true, skips format validation and Brass lookup — accepts any non-empty input (e.g. external emails) */
  skipValidation?: boolean;
  /** Custom placeholder text */
  placeholder?: string;
  /** Domain-specific validation that returns an inline error message or null when accepted. */
  validateEntry?: (value: string) => Promise<string | null>;
  /** Prefix applied to the input and add-button test ids when multiple lists coexist. */
  testIdPrefix?: string;
  /** Prevents additions and removals while keeping the list visible. */
  disabled?: boolean;
}

export const FormSharedUserInput: React.FC<FormSharedUserInputProps> = (props) => {
  const {
    control,
    currentUser,
    disabled = false,
    label: customLabel,
    labelAddon,
    name,
    placeholder: customPlaceholder,
    required = false,
    requireEmail = false,
    skipValidation = false,
    testIdPrefix = '',
    validateEntry,
  } = props;

  const { t } = useTranslation();
  const { field } = useController({
    control,
    name,
    rules: {
      required: required
        ? { value: true, message: t('form_validation_required_field') }
        : undefined,
    },
  });

  const [inputValue, setInputValue] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isValidating, setIsValidating] = useState(false);
  const [triggerValidate] = useLazyValidateAliasQuery();

  const onSubmit = async () => {
    if (disabled) return;

    const sanitizedInput = inputValue.trim().toLowerCase();
    const shouldRequireEmail = requireEmail && !skipValidation;
    setError(null);

    if (!sanitizedInput) return;

    const formatRegex = shouldRequireEmail ? EMAIL_REGEX : ALIAS_REGEX;
    if (!skipValidation && !formatRegex.test(sanitizedInput)) {
      setError(t(shouldRequireEmail
        ? 'shared_user_invalid_email_format'
        : 'shared_user_invalid_alias_format'));
      return;
    }

    const validationAlias = shouldRequireEmail
      ? sanitizedInput.split('@', 1)[0]
      : sanitizedInput;
    const storedValue = sanitizedInput;

    if (field.value?.includes(storedValue)) {
      setError(t('shared_user_duplicate_alias'));
      return;
    }

    const normalizedCurrentUser = currentUser?.toLowerCase();
    if (normalizedCurrentUser === sanitizedInput || normalizedCurrentUser === validationAlias) {
      setError(t('shared_user_self_add'));
      return;
    }

    if (validateEntry !== undefined || !skipValidation) {
      setIsValidating(true);
      try {
        if (validateEntry) {
          const validationMessage = await validateEntry(sanitizedInput);
          if (validationMessage !== null) {
            setError(validationMessage);
            return;
          }
        } else {
          const result = await triggerValidate(validationAlias).unwrap();
          if (!result.valid) {
            setError(t('shared_user_alias_not_found'));
            return;
          }
        }
      } catch {
        setError(t('shared_user_validation_error'));
        return;
      } finally {
        setIsValidating(false);
      }
    }

    field.onChange(field.value ? [...field.value, storedValue] : [storedValue]);
    setInputValue('');
  };

  const inputLabel = customLabel ?? t('program_shared_with_header');
  const inputId = labelAddon ? `${testIdPrefix}userId-input` : undefined;
  const inputRow = (
    <Row alignmentHorizontal="justify" alignmentVertical="bottom" widths={['fill']}>
      <Input
        aria-label={labelAddon ? inputLabel : undefined}
        data-testid={`${testIdPrefix}userId`}
        disabled={disabled}
        id={inputId}
        label={labelAddon ? undefined : inputLabel}
        placeholder={customPlaceholder ?? t(requireEmail
          ? 'program_user_email_placeholder'
          : 'program_user_alias_placeholder')}
        required={required}
        value={inputValue}
        onChange={(value: string) => {
          setInputValue(value);
          setError(null);
        }}
      />
      <Button
        data-testid={`${testIdPrefix}AddUser`}
        disabled={disabled || !inputValue || isValidating}
        onClick={onSubmit}
      >
        {isValidating ? <Loader size="small" /> : t('add_user_button')}
      </Button>
    </Row>
  );

  return (
    <Column>
      {labelAddon
        ? (
          <Column spacing="200">
            <Row alignmentVertical="center" spacing="100">
              <Text htmlFor={inputId} tag="label" type="b200">
                {inputLabel}
                {required && <span aria-hidden="true">*</span>}
              </Text>
              {labelAddon}
            </Row>
            {inputRow}
          </Column>
          )
        : inputRow}
      {error && (
        <Alert size="small" type="error">
          {error}
        </Alert>
      )}
      <TagCloud
        disabled={disabled}
        tags={field?.value ?? []}
        onClearAllClicked={() => {
          field.onChange([]);
        }}
        onTagClicked={(idx: number) => {
          field.onChange(field.value.toSpliced(idx, 1));
        }}
      />
    </Column>
  );
};
